/**
 * Per-shell DOM id scoping for VariantB.
 *
 * QuestionFrame renders its desktop and mobile shells at once and hides one with CSS, so a fixed
 * id inside lands in the DOM twice and lookups find the hidden desktop copy. Each shell provides
 * its name here: the desktop copy keeps the plain `x` and the mobile copy becomes `x_mobile`.
 * Outside a shell, no suffix is added.
 */

import React, { createContext, useContext } from "react";

export type Shell = "desktop" | "mobile";

export const ShellContext = createContext<Shell | undefined>(undefined);

export const useShell = (): Shell | undefined => useContext(ShellContext);

/** WL3 runs this same code, so SRE needs its `wl2_` ids renamed to tell the funnels apart. */
export type Funnel = "wl2" | "wl3";

export const FunnelContext = createContext<Funnel>("wl2");

export const useFunnel = (): Funnel => useContext(FunnelContext);

/** Only the `wl2_` prefix is per funnel; recipe ids (`answer_`, `question_`) are shared. */
export const funnelId = (base: string, funnel: Funnel): string =>
  funnel === "wl3" && base.startsWith("wl2_") ? `wl3_${base.slice(4)}` : base;

const scoped = (base: string, funnel: Funnel, shell: Shell | undefined): string => {
  const id = funnelId(base, funnel);
  return shell === "mobile" ? `${id}_mobile` : id;
};

/** Undefined passes through, so components with an optional id prop can call this freely. */
export function useShellId(base: string): string;
export function useShellId(base: string | undefined): string | undefined;
export function useShellId(base: string | undefined): string | undefined {
  const shell = useContext(ShellContext);
  const funnel = useContext(FunnelContext);
  return base ? scoped(base, funnel, shell) : base;
}

/** For ids built inside a loop, where you cannot call a hook. Falsy bases pass through. */
export const useShellIdFn = (): ((base: string) => string) => {
  const shell = useShell();
  const funnel = useContext(FunnelContext);
  return (base) => (base ? scoped(base, funnel, shell) : base);
};

interface ShellScopedIdProps {
  base: string | undefined;
  children: (id: string | undefined) => React.ReactNode;
}

/** For call sites above the provider, which would read undefined from the hook. */
export const ShellScopedId: React.FC<ShellScopedIdProps> = ({ base, children }) => (
  <>{children(useShellId(base))}</>
);
